import React, { useState, useMemo, Suspense, lazy } from 'react';
import { X, Plus } from 'lucide-react';
import ReportSelectorPopup from './ReportSelectorPopup';
import { DashboardDateManager } from './dashboardManager';
import logger from '../../utils/logger';

// 🚀 LAZY LOAD ALL REPORT COMPONENTS
const CallLeaderboardReport = lazy(() => import('./reports/CallLeaderboardReport'));
const UserPerformanceReport = lazy(() => import('./reports/UserPerformanceReport'));
const CallLogsReport = lazy(() => import('./reports/CallLogsReport'));
const SalesTargetTrackingReport = lazy(() => import('./reports/SalesTargetTrackingReport'));
const CustomFieldReport = lazy(() => import('./reports/CustomFieldReport'));
const QuotationStatusReport = lazy(() => import('./reports/QuotationStatusReport'));
const SalesFunnelReport = lazy(() => import('./reports/SalesFunnelReport'));
const LeadsBySourceReport = lazy(() => import('./reports/LeadsBySourceReport'));
const LeadsByLabelsReport = lazy(() => import('./reports/LeadsByLabelsReport'));
const TaskStatusReport = lazy(() => import('./reports/TaskStatusReport'));
const UserSyncStatusReport = lazy(() => import('./reports/UserSyncStatusReport'));
const ActivitySummaryReport = lazy(() => import('./reports/ActivitySummaryReport'));
const TrendsReport = lazy(() => import('./reports/TrendsReport'));
const DashboardLeadAssignmentTable = lazy(() => import('./DashboardLeadAssignmentTable'));

interface ReportTab {
  id: string;
  title: string;
  reportType: string;
  loaded: boolean; // 🎯 Track if component has been loaded
}

interface ReportTabsManagerProps {
  onClose: () => void;
  fromDate?: string;
  toDate?: string;
  selectedUserId?: string;
  selectedTeamId?: string;
  isOrganization?: boolean;
  initialReportId?: string;
  initialReportTitle?: string;
  visibilityConfig?: {
    reports?: Record<string, boolean>;
  };
}

// 🔄 Loading Spinner Component
const ReportLoadingSpinner = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px'
  }}>
    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
    <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading report...</p>
  </div>
);

const ReportTabsManager: React.FC<ReportTabsManagerProps> = ({
  onClose,
  fromDate,
  toDate,
  selectedUserId = "",
  selectedTeamId = "",
  isOrganization = false,
  initialReportId,
  initialReportTitle,
  visibilityConfig
}) => {
  // 🎯 USE DASHBOARD MANAGER for default dates
  const defaultRange = useMemo(() => {
    const dateManager = DashboardDateManager.getInstance();
    return dateManager.calculateDateRange('7d');
  }, []);

  const effectiveFromDate = fromDate || defaultRange.from;
  const effectiveToDate = toDate || defaultRange.to;

  const [tabs, setTabs] = useState<ReportTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>('');
  const [showReportSelector, setShowReportSelector] = useState(false);

  // 🚀 Auto-open visible reports on mount
  React.useEffect(() => {
    logger.log('🔍 [ReportTabsManager] useEffect triggered');
    logger.log('🔍 [ReportTabsManager] visibilityConfig:', visibilityConfig);
    logger.log('🔍 [ReportTabsManager] visibilityConfig?.reports:', visibilityConfig?.reports);

    const reportsToOpen: Array<{ id: string; title: string }> = [];

    // Check if we have an initial report to open
    if (initialReportId && initialReportTitle) {
      logger.log('📌 [ReportTabsManager] Opening initial report:', initialReportId, initialReportTitle);
      reportsToOpen.push({ id: initialReportId, title: initialReportTitle });
    }

    // Auto-open reports based on visibility preferences
    if (visibilityConfig?.reports) {
      const reportMapping: Record<string, string> = {
        trends: 'Trends & Analytics', // ⭐ First - default tab
        userPerformance: 'User Performance Report',
        salesFunnel: 'Sales Funnel Analysis',
        callLeaderboard: 'Call Leaderboard',
        callLogs: 'Call Logs Report',
        leadAssignment: 'Lead Assignment Report',
        activitySummary: 'Activity Summary',
        leadSource: 'Lead Source Analysis',
        leadByLabels: 'Leads by Labels',
        salesTarget: 'Sales Target Tracking',
        taskStatus: 'Task Status Report',
        customFields: 'Custom Field Analysis',
        quotationStatus: 'Quotation Status Report',
        userSyncStatus: 'User Sync Status',
      };

      logger.log('🔍 [ReportTabsManager] Checking visibility for reports...');

      // Iterate through reportMapping order (not visibilityConfig order)
      // This ensures trends comes first if enabled
      Object.entries(reportMapping).forEach(([key, title]) => {
        const reportsConfig = visibilityConfig?.reports;
        if (!reportsConfig) return;

        const visible = reportsConfig[key as keyof typeof reportsConfig];
        logger.log(`  - ${key}: ${visible}`);

        if (visible) {
          // Convert camelCase to kebab-case for report IDs
          const reportId = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          const reportTitle = title;

          // Don't add duplicates
          if (!reportsToOpen.find(r => r.id === reportId)) {
            logger.log(`✅ [ReportTabsManager] Auto-opening: ${reportId} (${reportTitle})`);
            reportsToOpen.push({ id: reportId, title: reportTitle });
          }
        }
      });
    } else {
      console.warn('⚠️ [ReportTabsManager] No visibilityConfig.reports found!');
    }

    logger.log('📊 [ReportTabsManager] Total reports to open:', reportsToOpen.length, reportsToOpen);

    // Open all visible reports
    if (reportsToOpen.length > 0) {
      const newTabs = reportsToOpen.map(report => ({
        id: report.id,
        title: report.title,
        reportType: report.id,
        loaded: false, // 🎯 Not loaded yet - will load on tab click
      }));

      logger.log('📊 [ReportTabsManager] Creating tabs:', newTabs);
      setTabs(newTabs);
      // Set first tab as active (it will load immediately)
      if (newTabs.length > 0) {
        setActiveTabId(newTabs[0].id);
        // Mark first tab as loaded since it's active
        newTabs[0].loaded = true;
        logger.log('✅ [ReportTabsManager] Active tab set to:', newTabs[0].id);
      }
    } else {
      logger.log('📊 [ReportTabsManager] No reports to auto-open');
    }
  }, [initialReportId, initialReportTitle, visibilityConfig]);

  // 🎯 Handle tab click - load component if not already loaded
  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId);

    // Mark tab as loaded when clicked
    setTabs(prevTabs =>
      prevTabs.map(tab =>
        tab.id === tabId ? { ...tab, loaded: true } : tab
      )
    );
  };

  function handleReportSelect(reportId: string, reportTitle: string) {
    // Check if tab already exists
    const existingTab = tabs.find(tab => tab.id === reportId);
    if (existingTab) {
      handleTabClick(reportId);
      return;
    }

    // Create new tab
    const newTab: ReportTab = {
      id: reportId,
      title: reportTitle,
      reportType: reportId,
      loaded: false, // 🎯 Not loaded until tab is clicked
    };

    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabId(reportId);
    // Mark as loaded since we're switching to it
    newTab.loaded = true;
  }

  // 🚀 LAZY LOADED REPORT COMPONENT RENDERER
  function getReportComponent(reportId: string, reportTitle: string, shouldLoad: boolean) {
    const reportProps = {
      fromDate: effectiveFromDate,
      toDate: effectiveToDate,
      selectedUserId,
      selectedTeamId,
      isOrganization
    };

    // 🎯 Don't render component until tab is loaded
    if (!shouldLoad) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <p>Click to load this report...</p>
        </div>
      );
    }

    // 🚀 Lazy load report components with Suspense
    switch (reportId) {
      case 'call-leaderboard':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <CallLeaderboardReport {...reportProps} />
          </Suspense>
        );

      case 'call-logs':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <CallLogsReport {...reportProps} />
          </Suspense>
        );

      case 'user-performance':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <UserPerformanceReport {...reportProps} />
          </Suspense>
        );

      case 'lead-assignment':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <DashboardLeadAssignmentTable
              fromDate={effectiveFromDate}
              toDate={effectiveToDate}
              organization={isOrganization}
              userId={selectedUserId}
              selectedTeamId={selectedTeamId}
              title="Lead Assignment Distribution"
            />
          </Suspense>
        );

      case 'task-status':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <TaskStatusReport {...reportProps} />
          </Suspense>
        );

      case 'sales-funnel':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <SalesFunnelReport {...reportProps} />
          </Suspense>
        );

      case 'sales-target':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <SalesTargetTrackingReport {...reportProps} />
          </Suspense>
        );

      case 'custom-fields':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <CustomFieldReport {...reportProps} />
          </Suspense>
        );

      case 'quotation-status':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <QuotationStatusReport {...reportProps} />
          </Suspense>
        );

      case 'lead-source':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <LeadsBySourceReport {...reportProps} />
          </Suspense>
        );

      case 'lead-by-labels':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <LeadsByLabelsReport {...reportProps} />
          </Suspense>
        );

      case 'user-sync-status':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <UserSyncStatusReport {...reportProps} />
          </Suspense>
        );

      case 'activity-summary':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <ActivitySummaryReport {...reportProps} />
          </Suspense>
        );

      case 'trends':
        return (
          <Suspense fallback={<ReportLoadingSpinner />}>
            <TrendsReport {...reportProps} />
          </Suspense>
        );

      default:
        return <div style={{ padding: '20px', color: '#ef4444' }}>Report not found: {reportId}</div>;
    }
  }

  const closeTab = (tabId: string) => {
    const newTabs = tabs.filter(tab => tab.id !== tabId);
    setTabs(newTabs);

    // If we closed the active tab, switch to the first remaining tab or empty
    if (activeTabId === tabId) {
      if (newTabs.length > 0) {
        setActiveTabId(newTabs[0].id);
      } else {
        setActiveTabId('');
      }
    }
  };

  const openReportSelector = () => {
    setShowReportSelector(true);
  };

  const handleReportSelectorClose = () => {
    setShowReportSelector(false);
  };

  return (
    <div style={{
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '600px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Header - No close button (always visible) */}
      <div style={{
        borderBottom: '1px solid #e5e7eb',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
          Business Reports
        </h3>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          Click the <strong>+</strong> button to add more reports
        </div>
      </div>

      {/* Tabs Header */}
      <div style={{
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        minHeight: '40px',
        paddingLeft: '12px'
      }}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              backgroundColor: activeTabId === tab.id ? 'white' : 'transparent',
              borderTop: activeTabId === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
              borderLeft: '1px solid #e5e7eb',
              borderRight: '1px solid #e5e7eb',
              borderBottom: activeTabId === tab.id ? '1px solid white' : '1px solid #e5e7eb',
              cursor: 'pointer',
              marginRight: '2px',
              position: 'relative',
              top: activeTabId === tab.id ? '1px' : '0'
            }}
            onClick={() => handleTabClick(tab.id)}
          >
            <span style={{
              fontSize: '14px',
              fontWeight: activeTabId === tab.id ? '600' : '400',
              color: activeTabId === tab.id ? '#1f2937' : '#6b7280',
              marginRight: '8px'
            }}>
              {tab.title}
              {!tab.loaded && activeTabId !== tab.id && ' 💤'}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeTab(tab.id);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                minWidth: '28px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                borderRadius: '6px',
                color: '#9ca3af',
                transition: 'all 0.15s ease',
                padding: '0',
                marginLeft: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fee2e2';
                e.currentTarget.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#9ca3af';
              }}
              title="Close report"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
        ))}

        {/* Add New Tab Button */}
        <button
          onClick={openReportSelector}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            minWidth: '36px',
            border: '1px solid #d1d5db',
            backgroundColor: 'white',
            cursor: 'pointer',
            borderRadius: '6px',
            marginLeft: '12px',
            color: '#6b7280',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#3b82f6';
            e.currentTarget.style.borderColor = '#3b82f6';
            e.currentTarget.style.color = 'white';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.25)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.color = '#6b7280';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          title="Add new report"
        >
          <Plus size={18} strokeWidth={2.5} />
        </button>
      </div>

      {/* Tab Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#f8fafc',
        minHeight: '500px'
      }}>
        {tabs.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '40px',
            textAlign: 'center'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: '#374151'
            }}>
              Welcome to Business Reports
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              Click the <strong>+</strong> button above to select and open reports in tabs for easy navigation and comparison.
            </p>
            <button
              onClick={openReportSelector}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plus size={20} />
              Browse Reports
            </button>
          </div>
        ) : (
          tabs.map((tab) => (
            <div
              key={tab.id}
              style={{
                display: activeTabId === tab.id ? 'block' : 'none',
                height: '100%',
                padding: '16px'
              }}
            >
              {/* Always render active tab's component, mark others as loaded when they become active */}
              {activeTabId === tab.id ? getReportComponent(tab.id, tab.title, true) : null}
            </div>
          ))
        )}
      </div>

      {/* Report Selector Popup */}
      {showReportSelector && (
        <ReportSelectorPopup
          isOpen={showReportSelector}
          onClose={handleReportSelectorClose}
          onReportSelect={(reportId: string, reportTitle: string) => {
            handleReportSelect(reportId, reportTitle);
            setShowReportSelector(false);
          }}
        />
      )}
    </div>
  );
};

export default ReportTabsManager;
